import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";

import { cn } from "./cn";

export const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border font-semibold no-underline transition-colors disabled:cursor-not-allowed disabled:opacity-55",
  {
    variants: {
      variant: {
        primary: "border-primary bg-primary text-primary-foreground hover:brightness-110",
        outline: "border-border bg-card text-foreground hover:border-primary",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:border-primary",
        ghost: "border-transparent bg-transparent text-foreground hover:bg-muted",
        destructive: "border-destructive bg-destructive text-destructive-foreground hover:brightness-110",
        link: "h-auto min-h-0 border-0 bg-transparent p-0 font-medium text-primary underline-offset-4 hover:underline",
      },
      size: {
        md: "min-h-11 px-[18px] text-[0.95rem]",
        sm: "min-h-9 px-3 text-[0.88rem]",
        lg: "min-h-12 px-6 text-base",
        icon: "size-10 p-0",
      },
    },
    compoundVariants: [{ variant: "link", className: "min-h-0 px-0" }],
    defaultVariants: { variant: "outline", size: "md" },
  },
);

export type ButtonProps = ComponentProps<"button"> & VariantProps<typeof buttonVariants> & { asChild?: boolean };

/** shadcn-style button; `asChild` renders the child (e.g. a Next.js Link) with button styles. */
export function Button({ className, variant, size, asChild = false, type, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return <Comp className={cn(buttonVariants({ variant, size }), className)} {...(asChild ? {} : { type: type ?? "button" })} {...props} />;
}
